import { useState, useCallback } from 'react';
import { bundlesApi } from '../api/bundles';
import type { BundleDiscount, CreateBundlePayload } from '../api/bundles';

export function useBundles() {
  const [bundles, setBundles] = useState<BundleDiscount[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchBundles = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await bundlesApi.list(p);
      setBundles(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load bundle discounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  const createBundle = useCallback(async (payload: CreateBundlePayload) => {
    setCreating(true);
    setError(null);
    try {
      const res = await bundlesApi.create(payload);
      setBundles((prev) => [res.data.data, ...prev]);
      setTotal((t) => t + 1);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Bundle discount creation failed.');
      return null;
    } finally {
      setCreating(false);
    }
  }, []);

  const deleteBundle = useCallback(async (id: number) => {
    try {
      await bundlesApi.destroy(id);
      setBundles((prev) => prev.filter((b) => b.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete bundle discount.');
    }
  }, []);

  return {
    bundles, loading, creating, error, total, page, lastPage,
    fetchBundles, createBundle, deleteBundle, setError,
  };
}
